import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, FlatList, Image, InteractionManager, ListRenderItem, StyleSheet, Text, View, ViewToken } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import Svg, { Circle, Defs, Line, LinearGradient, Polygon, Polyline, RadialGradient, Rect, Stop } from 'react-native-svg';
import { RootStackParamList } from '../navigation/types';
import { useSession } from '../session/SessionProvider';
import { getEpisodes, getHomeWatchRows, getGenres, getHomeRecommendations, getItems, getRecommendationShelves, getSeasons, getUserViews, homeRecommendationSeed, imageSource, JellyfinItem, reportRecommendationTelemetry } from '../api/jellyfin';
import { MatureTopNav, useBottomNavInset } from '../components/MatureTopNav';
import { MediaShelf, MediaShelfPresentation } from '../components/MediaShelf';
import { colors, getBrowseVisualTokens, radius, spacing, typography , systemSansFont} from '../theme/tokens';
import { FocusablePressable } from '../components/FocusablePressable';
import { GenreShelf } from '../components/GenreShelf';
import { StreamingServicesShelf } from '../components/StreamingServicesShelf';
import { getImageAccent } from '../utils/imageAccent';
import { defaultHomeConfig, NativeHomeConfig, readHomeConfig, saveHomeConfig } from '../storage/homeConfig';
import { buildHomeUserViewsSourceKey, mergeHomeConfigUserViews, orderHomeUserViews } from '../storage/homeUserViews';
import { useReferenceWindowDimensions } from '../utils/referenceViewport';
import { defaultPreferences, NativePreferences, readPreferences } from '../storage/preferences';
import {
    prefetchPlaybackInfoForItem,
    prefetchPlaybackItemMetadata,
} from '../player/playbackPrefetch';
import { buildDiscoveryRecommendationShelves, buildHomeRecommendationShelves, buildRecommendedMediaShelves, buildRecommendationShelves, orderRecommendationShelvesForProfile, RecommendationShelf } from '../recommendations/shelves';
import { composeHomeRowKeys, interleavePublicDiscoveryRows, removeRedundantHomeRows } from '../recommendations/homeComposition';
import { productProfile } from '../productProfile';
import { focusRegisteredNode, setNativeLazyFocusMove } from '../tvNavigation/registry';

type Props = NativeStackScreenProps<RootStackParamList, 'Home'>;
type HomeRow =
    | { key: string; kind: 'media'; title: string; items: JellyfinItem[]; landscape?: boolean; presentation?: MediaShelfPresentation; recommendation?: RecommendationShelf; libraryRoute?: { itemType: 'Movie' | 'Series'; libraryId: string; libraryName: string } }
    | { key: 'genres'; kind: 'genres'; items: JellyfinItem[] }
    | { key: 'streaming-services'; kind: 'streaming-services' };
const lastHeroItemByUser = new Map<string, string>();
const heroSessionSeedByUser = new Map<string, string>();

function userViewItemType(view: JellyfinItem): 'Movie' | 'Series' | undefined {
    const collectionType = view.CollectionType?.toLocaleLowerCase();
    if (collectionType === 'movies') return 'Movie';
    if (collectionType === 'tvshows') return 'Series';
    return undefined;
}

function ContinueWatchingGhost({ pagePadding, compact, cardWidth: requestedCardWidth }: { pagePadding: number; compact: boolean; cardWidth?: number }) {
    const bottomNavInset = useBottomNavInset();
    const cardWidth = requestedCardWidth ?? (compact ? 180 : 305);
    const placeholders = useMemo(() => Array.from({ length: compact ? 3 : 6 }, (_, index) => `continue-ghost-${index}`), [compact]);
    return <View style={styles.continueGhost}>
        <Text style={[typography.sectionTitle, compact ? styles.ghostTitleMobile : styles.ghostTitle, { marginHorizontal: pagePadding }]}>Continue Watching</Text>
        <FlatList
            horizontal
            scrollEnabled={false}
            data={placeholders}
            keyExtractor={(item) => item}
            contentContainerStyle={[{ paddingHorizontal: pagePadding, paddingTop: 17, gap: 8 }, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
            renderItem={() => <View style={{ width: cardWidth }}>
                <View style={[styles.ghostCard, { width: cardWidth, height: Math.round(cardWidth * 0.5625) }]} />
                <View style={[styles.ghostLine, { width: Math.round(cardWidth * 0.68) }]} />
                <View style={[styles.ghostLineShort, { width: Math.round(cardWidth * 0.42) }]} />
            </View>}
        />
    </View>;
}

function HeroActionButton({ kind, mobile, television, height, fontSize, onPress, tvPreferredFocus, firstRowFocusId }: { kind: 'play' | 'info'; mobile: boolean; television: boolean; height: number; fontSize: number; onPress: () => void; tvPreferredFocus?: boolean; firstRowFocusId?: string }) {
    const hoverProgress = useRef(new Animated.Value(0)).current;
    const animateHover = (active: boolean) => Animated.timing(hoverProgress, {
        toValue: active ? 1 : 0,
        duration: 150,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: false,
    }).start();
    const backgroundColor = kind === 'play'
        ? hoverProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(255,255,255,1)', 'rgba(255,255,255,0.75)'] })
        : hoverProgress.interpolate({ inputRange: [0, 1], outputRange: ['rgba(90,90,90,0.8)', 'rgba(90,90,90,0.5)'] });
    return (
        <FocusablePressable testID="tv-hero-control" focusId={`home.hero.${kind}`} focusGroup="home.hero" focusRole="hero" focusDestinations={{ ...(kind === 'play' ? { left: 'shell.rail.home', right: 'home.hero.info' } : { left: 'shell.rail.home', right: 'home.hero.play' }), ...(firstRowFocusId ? { down: firstRowFocusId } : {}) }} accessibilityRole="button" accessibilityLabel={kind === 'play' ? 'Play featured title' : 'More information about featured title'} onPress={onPress} onHoverIn={() => animateHover(true)} onHoverOut={() => animateHover(false)} tvPreferredFocus={tvPreferredFocus} style={(focused, pressed) => [kind === 'play' ? styles.playButton : styles.infoButton, mobile && (kind === 'play' ? styles.playButtonMobile : styles.infoButtonMobile), television && (kind === 'play' ? styles.playButtonTelevision : styles.infoButtonTelevision), { height }, focused && (television ? styles.heroActionFocusedTelevision : styles.heroActionFocused), pressed && styles.playButtonPressed]}>
            <Animated.View pointerEvents="none" style={[styles.heroActionFill, { backgroundColor }]} />
            <View pointerEvents="none" style={styles.heroActionContent}>
                {kind === 'play' ? <>
                    <Svg width={16} height={16} viewBox="0 0 24 24"><Polygon points="5,3 19,12 5,21" fill={colors.background} /></Svg>
                    <Text style={[styles.playText, { fontSize }]}>Play</Text>
                </> : <>
                    <Svg width={20} height={20} viewBox="0 0 24 24">
                        <Circle cx="12" cy="12" r="10" fill="none" stroke={colors.text} strokeWidth="2" />
                        <Line x1="12" y1="8" x2="12" y2="8" stroke={colors.text} strokeWidth="2" />
                        <Polyline points="11,12 12,12 12,16" fill="none" stroke={colors.text} strokeWidth="2" />
                    </Svg>
                    <Text style={[styles.infoText, { fontSize }]}>More Info</Text>
                </>}
            </View>
        </FocusablePressable>
    );
}

function hashHeroCandidate(value: string) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function selectSeededHeroItems(items: JellyfinItem[], seed: string, limit: number) {
    return items
        .filter((item) => Boolean(item.BackdropImageTags?.length))
        .sort((left, right) => {
            const leftId = left.Id ?? left.Name ?? '';
            const rightId = right.Id ?? right.Name ?? '';
            return hashHeroCandidate(`${seed}:${leftId}`) - hashHeroCandidate(`${seed}:${rightId}`) || leftId.localeCompare(rightId);
        })
        .slice(0, limit);
}

export function HomeScreen({ navigation }: Props) {
    const { width, height } = useReferenceWindowDimensions();
    const browseVisuals = getBrowseVisualTokens(width);
    const [logoFailed, setLogoFailed] = useState(false);
    const [logoValidated, setLogoValidated] = useState(false);
    const [logoAspect, setLogoAspect] = useState(3.2);
    const [heroAccent, setHeroAccent] = useState<string>(colors.brand);
    const [initialHeroReady, setInitialHeroReady] = useState(false);
    const [heroOffset, setHeroOffset] = useState(0);
    const [navSolid, setNavSolid] = useState(false);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);
    const [homeConfig, setHomeConfig] = useState<NativeHomeConfig>(defaultHomeConfig);
    const [preferences, setPreferences] = useState<NativePreferences>(defaultPreferences);
    const { credentials, user } = useSession();
    const queryClient = useQueryClient();
    const bottomNavInset = useBottomNavInset();
    const continueIntentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const continuePlaybackInfoInFlight = useRef(false);
    const recommendationSeed = useMemo(
        () => credentials ? homeRecommendationSeed(credentials) : 0,
        [credentials?.serverUrl, credentials?.userId],
    );
    const recommendationSessionKeyRef = useRef(
        `${credentials?.userId ?? 'anonymous'}:${Date.now()}:${recommendationSeed}`,
    );
    const recommendationSessionKey = recommendationSessionKeyRef.current;
    const impressedRecommendationRows = useRef(new Set<string>());
    const recommendationTelemetryContext = useRef({ credentials, recommendationSessionKey, recommendationSeed });
    recommendationTelemetryContext.current = { credentials, recommendationSessionKey, recommendationSeed };
    useEffect(() => { void Promise.all([readHomeConfig(), readPreferences()]).then(([home, savedPreferences]) => { setHomeConfig(home); setPreferences(savedPreferences); }); }, []);
    const userViews = useQuery({
        queryKey: ['home', 'user-views', credentials?.serverUrl, credentials?.userId],
        queryFn: () => getUserViews(credentials!),
        enabled: Boolean(credentials && productProfile.kind === 'public'),
        staleTime: 5 * 60 * 1000,
    });
    const orderedUserViews = useMemo(
        () => orderHomeUserViews(userViews.data ?? [], user?.Configuration?.OrderedViews),
        [user?.Configuration?.OrderedViews, userViews.data],
    );
    useEffect(() => {
        if (productProfile.kind !== 'public' || !credentials || !orderedUserViews.length) return;
        const defaultDisabledIds = user?.Configuration?.LatestItemsExcludes ?? [];
        const sourceKey = buildHomeUserViewsSourceKey(credentials.serverUrl, credentials.userId, orderedUserViews, defaultDisabledIds);
        setHomeConfig((current) => {
            const next = mergeHomeConfigUserViews(current, orderedUserViews, sourceKey, defaultDisabledIds);
            if (next === current) return current;
            void saveHomeConfig(next);
            return next;
        });
    }, [credentials, orderedUserViews, user?.Configuration?.LatestItemsExcludes]);
    // Stable across remounts so cached hero data survives back-navigation.
    // The transition layer briefly mounts Home again while returning from a
    // detail route; changing this key there would create a blank first frame.
    const heroSessionSeed = useMemo(() => {
        const key = credentials?.userId ?? 'anonymous';
        const existing = heroSessionSeedByUser.get(key);
        if (existing) return existing;
        const next = `${key}:${Date.now()}:${Math.random()}`;
        heroSessionSeedByUser.set(key, next);
        return next;
    }, [credentials?.userId]);
    const [initialHeroRandom] = useState(Math.random);
    const latestHeroId = useRef<string | undefined>();
    const heroBackdropOpacity = useRef(new Animated.Value(1)).current;
    const heroContentOpacity = useRef(new Animated.Value(1)).current;
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const heroTransitioning = useRef(false);
    const heroRevealPending = useRef(true);
    const continueResult = useQuery({ queryKey: ['home', 'watch-rows'], queryFn: () => getHomeWatchRows(credentials!, 20), enabled: Boolean(credentials) });
    useEffect(() => {
        if (!credentials) return;
        const task = InteractionManager.runAfterInteractions(() => {
            for (const item of (continueResult.data?.combined ?? []).slice(0, 6)) {
                if (item.Id) {
                    void prefetchPlaybackItemMetadata(queryClient, credentials, item.Id);
                }
            }
        });
        return () => task.cancel();
    }, [continueResult.data, credentials, queryClient]);
    useEffect(() => () => {
        if (continueIntentTimer.current) clearTimeout(continueIntentTimer.current);
    }, []);
    const handleContinueIntent = useCallback((item: JellyfinItem, active: boolean) => {
        if (continueIntentTimer.current) {
            clearTimeout(continueIntentTimer.current);
            continueIntentTimer.current = null;
        }
        if (
            !active ||
            !credentials ||
            !item.Id ||
            continuePlaybackInfoInFlight.current
        ) {
            return;
        }
        continueIntentTimer.current = setTimeout(() => {
            continueIntentTimer.current = null;
            continuePlaybackInfoInFlight.current = true;
            void prefetchPlaybackInfoForItem(
                queryClient,
                credentials,
                item.Id!,
                preferences.audioLanguage,
            ).catch(() => undefined).finally(() => {
                continuePlaybackInfoInFlight.current = false;
            });
        }, 650);
    }, [credentials, preferences.audioLanguage, queryClient]);
    const heroResult = useQuery({ queryKey: ['home', 'hero', heroSessionSeed], queryFn: async () => selectSeededHeroItems(await getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Movie,Series', SortBy: 'SimilarityScore,CommunityRating', SortOrder: 'Descending,Descending', Limit: 150, Fields: 'Overview,Genres,UserData,BackdropImageTags,ImageTags', EnableImages: true, EnableImageTypes: 'Backdrop,Logo,Primary', ImageTypeLimit: 1, EnableTotalRecordCount: false }), heroSessionSeed, 24), enabled: Boolean(credentials) });
    const primaryReady = !continueResult.isLoading && !heroResult.isLoading;
    const viewEnabled = (view: JellyfinItem) => homeConfig.libraryToggles.find((entry) => entry.libraryId === view.Id || entry.libraryName.toLocaleLowerCase() === view.Name?.toLocaleLowerCase())?.enabled !== false;
    const moviesEnabled = productProfile.kind === 'public' && userViews.data
        ? userViews.data.some((view) => view.CollectionType?.toLocaleLowerCase() === 'movies' && viewEnabled(view))
        : homeConfig.libraryToggles.find((entry) => entry.libraryName.toLowerCase() === 'movies')?.enabled !== false;
    const showsEnabled = productProfile.kind === 'public' && userViews.data
        ? userViews.data.some((view) => view.CollectionType?.toLocaleLowerCase() === 'tvshows' && viewEnabled(view))
        : homeConfig.libraryToggles.find((entry) => ['shows', 'tv shows'].includes(entry.libraryName.toLowerCase()))?.enabled !== false;
    const results = useQueries({ queries: [
        { queryKey: ['home', 'top-movies'], queryFn: () => getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Movie', Filters: 'IsUnplayed', SortBy: 'CommunityRating', SortOrder: 'Descending', Limit: 20, Fields: 'Overview,UserData,BackdropImageTags' }) },
        { queryKey: ['home', 'top-series'], queryFn: () => getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Series', Filters: 'IsUnplayed', SortBy: 'CommunityRating', SortOrder: 'Descending', Limit: 20, Fields: 'Overview,UserData,BackdropImageTags' }) },
        { queryKey: ['home', 'watch-again'], queryFn: () => getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Movie,Series', Filters: 'IsPlayed', SortBy: 'DatePlayed', SortOrder: 'Descending', Limit: 15, Fields: 'Overview,UserData,BackdropImageTags' }) },
        { queryKey: ['home', 'recent-movies'], queryFn: () => getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Movie', SortBy: 'DateCreated', SortOrder: 'Descending', Limit: 20, Fields: 'Overview,UserData,BackdropImageTags' }) },
        { queryKey: ['home', 'recent-series'], queryFn: () => getItems(credentials!, { Recursive: true, IncludeItemTypes: 'Series', SortBy: 'DateCreated', SortOrder: 'Descending', Limit: 20, Fields: 'Overview,UserData,BackdropImageTags' }) },
    ].map((query) => ({ ...query, enabled: Boolean(credentials) && primaryReady })) });
    const enabledLibraryViews = useMemo(() => orderedUserViews.flatMap((view) => {
        const itemType = userViewItemType(view);
        if (!itemType || !view.Id || !view.Name) return [];
        const toggle = homeConfig.libraryToggles.find((entry) => entry.libraryId === view.Id || entry.libraryName.toLocaleLowerCase() === view.Name?.toLocaleLowerCase());
        return toggle?.enabled === false ? [] : [{ view, itemType }];
    }), [homeConfig.libraryToggles, orderedUserViews]);
    const libraryResults = useQueries({ queries: enabledLibraryViews.map(({ view, itemType }) => ({
        queryKey: ['home', 'server-library', view.Id, itemType],
        queryFn: () => getItems(credentials!, {
            ParentId: view.Id!,
            Recursive: true,
            IncludeItemTypes: itemType,
            SortBy: 'DateCreated',
            SortOrder: 'Descending',
            Limit: 20,
            Fields: 'Overview,UserData,BackdropImageTags',
            EnableImages: true,
            EnableUserData: true,
            EnableTotalRecordCount: false,
        }),
        enabled: Boolean(credentials && primaryReady && productProfile.kind === 'public'),
        staleTime: 5 * 60 * 1000,
    })) });
    const homeFeed = useQuery({
        queryKey: ['home', 'feed', recommendationSeed, moviesEnabled, showsEnabled],
        queryFn: () => getHomeRecommendations(credentials!, {
            rowLimit: 12,
            itemLimit: 12,
            seed: recommendationSeed,
            includeMovies: moviesEnabled,
            includeSeries: showsEnabled,
        }),
        enabled: Boolean(credentials && primaryReady && preferences.showRecommendations && productProfile.aggressiveOptimizationsEnabled),
        staleTime: 5 * 60 * 1000,
        retry: (failureCount, error) => (error as Error & { status?: number }).status !== 404 && failureCount < 2,
    });
    const useLegacyRecommendations = !productProfile.aggressiveOptimizationsEnabled || homeFeed.isError;
    const movieTaste = useQuery({
        queryKey: ['home', 'taste', 'Movie', recommendationSeed],
        queryFn: () => getRecommendationShelves(credentials!, 'Movie', 12, 12, (recommendationSeed ^ 0x4d4f5649) >>> 0),
        enabled: Boolean(credentials && primaryReady && preferences.showRecommendations && moviesEnabled && useLegacyRecommendations),
        staleTime: 5 * 60 * 1000,
    });
    const seriesTaste = useQuery({
        queryKey: ['home', 'taste', 'Series', recommendationSeed],
        queryFn: () => getRecommendationShelves(credentials!, 'Series', 12, 12, (recommendationSeed ^ 0x53455249) >>> 0),
        enabled: Boolean(credentials && primaryReady && preferences.showRecommendations && showsEnabled && useLegacyRecommendations),
        staleTime: 5 * 60 * 1000,
    });
    const genres = useQuery({ queryKey: ['home', 'genres', 'video'], queryFn: () => getGenres(credentials!, 'Movie,Series'), enabled: Boolean(credentials) && primaryReady });
    const publicGenreCandidates = useMemo(() => {
        if (productProfile.kind !== 'public') return [];
        const counts = new Map<string, number>();
        for (const item of [...(results[3].data ?? []), ...(results[4].data ?? []), ...(results[0].data ?? []), ...(results[1].data ?? [])]) {
            for (const name of item.Genres ?? []) {
                const key = name.trim().toLocaleLowerCase();
                if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        }
        return (genres.data ?? [])
            .filter((genre) => Boolean(genre.Id && genre.Name && counts.has(genre.Name.trim().toLocaleLowerCase())))
            .sort((left, right) => (counts.get(right.Name?.trim().toLocaleLowerCase() ?? '') ?? 0) - (counts.get(left.Name?.trim().toLocaleLowerCase() ?? '') ?? 0))
            .slice(0, 4);
    }, [genres.data, results[0].data, results[1].data, results[3].data, results[4].data]);
    const publicGenreResults = useQueries({ queries: publicGenreCandidates.map((genre) => ({
        queryKey: ['home', 'public-genre', genre.Id, moviesEnabled, showsEnabled],
        queryFn: () => getItems(credentials!, {
            Recursive: true,
            GenreIds: genre.Id!,
            IncludeItemTypes: [moviesEnabled ? 'Movie' : '', showsEnabled ? 'Series' : ''].filter(Boolean).join(','),
            SortBy: 'CommunityRating,SortName',
            SortOrder: 'Descending,Ascending',
            Limit: 18,
            Fields: 'Overview,UserData,BackdropImageTags',
            EnableImages: true,
            EnableUserData: true,
            EnableTotalRecordCount: false,
        }),
        enabled: Boolean(credentials && primaryReady && preferences.showRecommendations && (moviesEnabled || showsEnabled)),
        staleTime: 5 * 60 * 1000,
    })) });
    const tasteShelves = useMemo(() => {
        const candidates = homeFeed.data
            ? buildHomeRecommendationShelves(homeFeed.data)
            : useLegacyRecommendations
                ? buildRecommendationShelves(movieTaste.data ?? [], seriesTaste.data ?? [], recommendationSeed)
                : [];
        const seenTitles = new Set<string>();
        const seenItemSets = new Set<string>();
        return orderRecommendationShelvesForProfile(candidates, homeFeed.data?.ProfileMode).filter((shelf) => {
            const titleKey = shelf.title.trim().toLowerCase();
            const itemSetKey = shelf.items
                .map((item) => item.Id)
                .filter(Boolean)
                .slice(0, 6)
                .sort()
                .join(':');
            if (seenTitles.has(titleKey) || (itemSetKey && seenItemSets.has(itemSetKey))) return false;
            seenTitles.add(titleKey);
            if (itemSetKey) seenItemSets.add(itemSetKey);
            return true;
        }).map((shelf, shelfPosition) => ({ ...shelf, shelfPosition }));
    }, [homeFeed.data, movieTaste.data, recommendationSeed, seriesTaste.data, useLegacyRecommendations]);
    const recommendedMediaRows = useMemo<HomeRow[]>(() => buildRecommendedMediaShelves(tasteShelves)
        .map((recommendation) => ({
            key: recommendation.key,
            kind: 'media',
            title: recommendation.title,
            items: recommendation.items,
            recommendation,
        })), [tasteShelves]);
    const heroCandidates = heroResult.data ?? [];
    const rememberedHeroIndex = heroCandidates.findIndex((item) => item.Id === lastHeroItemByUser.get(credentials?.userId ?? ''));
    const baseHeroIndex = rememberedHeroIndex >= 0
        ? (rememberedHeroIndex + 1) % Math.max(heroCandidates.length, 1)
        : Math.min(Math.max(heroCandidates.length - 1, 0), Math.floor(initialHeroRandom * heroCandidates.length));
    const heroVisualIndex = (baseHeroIndex + heroOffset + heroCandidates.length) % Math.max(heroCandidates.length, 1);
    const hero = heroCandidates[heroVisualIndex] ?? heroResult.data?.[0];
    const heroPageCount = Math.min(5, heroCandidates.length);
    const heroCarouselPosition = heroPageCount > 0
        ? ((heroOffset % heroPageCount) + heroPageCount) % heroPageCount
        : 0;
    latestHeroId.current = hero?.Id;
    const libraryRows = useMemo<HomeRow[]>(() => enabledLibraryViews.flatMap(({ view, itemType }, index) => {
        const items = libraryResults[index]?.data ?? [];
        if (!view.Id || !view.Name || items.length < 4) return [];
        return [{
            key: `server-library:${view.Id}`,
            kind: 'media' as const,
            title: `Recently added in ${view.Name}`,
            items,
            libraryRoute: { itemType, libraryId: view.Id, libraryName: view.Name },
        }];
    }), [enabledLibraryViews, libraryResults]);
    const publicGenreRows = useMemo<HomeRow[]>(() => publicGenreCandidates.flatMap((genre, index) => {
        const items = publicGenreResults[index]?.data ?? [];
        if (!genre.Id || !genre.Name || items.length < 4) return [];
        return [{ key: `public-genre:${genre.Id}`, kind: 'media' as const, title: `Popular ${genre.Name}`, items }];
    }), [publicGenreCandidates, publicGenreResults]);
    const availableRows = useMemo<HomeRow[]>(() => [
        { key: 'continue-combined', kind: 'media', title: 'Continue Watching', items: continueResult.data?.combined ?? [], landscape: true, presentation: 'continue' },
        { key: 'continue-watching', kind: 'media', title: 'Continue Watching', items: continueResult.data?.continueWatching ?? [], landscape: true, presentation: 'continue' },
        { key: 'next-up', kind: 'media', title: 'Next Up', items: continueResult.data?.nextUp ?? [], landscape: true, presentation: 'continue' },
        { key: 'top-movies', kind: 'media', title: 'Top Rated Movies', items: results[0].data ?? [] },
        { key: 'top-series', kind: 'media', title: 'Top Rated TV Shows', items: results[1].data ?? [] },
        { key: 'watch-again', kind: 'media', title: 'Watch it again', items: results[2].data ?? [] },
        { key: 'recent-movies', kind: 'media', title: 'Recently Added in Movies', items: results[3].data ?? [] },
        { key: 'recent-series', kind: 'media', title: 'Recently Added in Series', items: results[4].data ?? [] },
        { key: 'genres', kind: 'genres', items: genres.data ?? [] },
        { key: 'streaming-services', kind: 'streaming-services' },
        ...libraryRows,
    ], [continueResult.data, genres.data, libraryRows, results[0].data, results[1].data, results[2].data, results[3].data, results[4].data]);
    const discoveryRecommendations = useMemo(
        () => buildDiscoveryRecommendationShelves(tasteShelves),
        [tasteShelves],
    );
    const discoveryRows = useMemo<HomeRow[]>(() => {
        const contextual: HomeRow[] = discoveryRecommendations.map((recommendation) => ({
            key: recommendation.key,
            kind: 'media' as const,
            title: recommendation.title,
            items: recommendation.items,
            recommendation,
        }));
        const ordered = productProfile.kind !== 'public'
            ? contextual
            : interleavePublicDiscoveryRows(contextual, publicGenreRows);
        return ordered.slice(0, Math.min(12, Math.max(0, homeConfig.dynamicSectionLimit || 12)));
    }, [discoveryRecommendations, homeConfig.dynamicSectionLimit, publicGenreRows]);
    const rows = useMemo<HomeRow[]>(() => {
        const allRows = [...availableRows, ...recommendedMediaRows, ...discoveryRows];
        const rowByKey = new Map(allRows.map((row) => [row.key, row]));
        const composedRows = composeHomeRowKeys({
            sections: homeConfig.sections,
            discoveryKeys: preferences.showRecommendations ? discoveryRows.map((row) => row.key) : [],
            becauseYouWatchedKeys: preferences.showRecommendations ? discoveryRows.filter((row) => row.kind === 'media' && row.recommendation?.recommendationType === 'SimilarToRecentlyPlayed').map((row) => row.key) : [],
            libraryKeys: libraryRows.map((row) => row.key),
            recommendedMovieKey: recommendedMediaRows.find((row) => row.key === 'home-recommended:Movie')?.key,
            recommendedShowKey: recommendedMediaRows.find((row) => row.key === 'home-recommended:Series')?.key,
            primaryReady,
            moviesEnabled,
            showsEnabled,
        }).flatMap((key) => rowByKey.get(key) ?? []);
        return removeRedundantHomeRows(composedRows);
    }, [availableRows, discoveryRows, homeConfig.sections, libraryRows, moviesEnabled, preferences.showRecommendations, primaryReady, recommendedMediaRows, showsEnabled]);
    const pagePadding = browseVisuals.pagePadding;
    const television = browseVisuals.viewport === 'television';
    // TV layouts get the centered hero treatment by default; the preference
    // still enables it on larger desktop layouts as well.
    const centeredHero = preferences.centeredHero === 'on' || (preferences.centeredHero === 'auto' && television);
    const televisionContinueCardWidth = television
        ? Math.min(360, Math.max(280, Math.round(height * 0.36)))
        : undefined;
    const firstHomeMediaRow = rows.find((row): row is Extract<HomeRow, { kind: 'media' }> => row.kind === 'media' && row.items.length > 0);
    const firstRowFocusId = firstHomeMediaRow?.items[0]?.Id
        ? `shelf.${firstHomeMediaRow.key}.${firstHomeMediaRow.items[0].Id}`
        : undefined;
    const mediaRowFocusIds = useMemo(() => rows
        .filter((row): row is Extract<HomeRow, { kind: 'media' }> => row.kind === 'media' && row.items.length > 0)
        .map((row) => ({ key: row.key, id: `shelf.${row.key}.${row.items[0].Id}` })), [rows]);
    const firstContinueRow = rows.find((row): row is Extract<HomeRow, { kind: 'media' }> => row.kind === 'media' && row.presentation === 'continue' && row.items.length > 0);
    const heroHeight = width <= 760
        ? 468
        : television
            ? Math.max(490, Math.min(880, height - 270))
            : Math.max(520, Math.min(860, height - 240));
    const heroOverlap = width <= 760 ? 12 : 34;
    const heroBlendDepth = width <= 760 ? 0 : Math.max(240, Math.min(300, Math.round(height * 0.28)));
    const vignetteHeight = heroHeight + heroBlendDepth + (width <= 760 ? 0 : 40);
    const openItem = useCallback((item: JellyfinItem) => item.Id && navigation.navigate('Item', { itemId: item.Id, previewItem: item }), [navigation]);
    const playItem = useCallback(async (item: JellyfinItem) => {
        if (!item.Id) return;
        // Resolve a concrete episode here so the homepage Play action really
        // starts playback. The details action remains the explicit way to
        // open the episode picker.
        if (item.Type === "Series") {
            try {
                const seasons = await getSeasons(credentials!, item.Id);
                const season = [...seasons].sort((left, right) => {
                    const leftExtras = left.IndexNumber === 0 || /^Season 0$/i.test(left.Name ?? '');
                    const rightExtras = right.IndexNumber === 0 || /^Season 0$/i.test(right.Name ?? '');
                    if (leftExtras !== rightExtras) return leftExtras ? 1 : -1;
                    return (left.IndexNumber ?? Number.MAX_SAFE_INTEGER) - (right.IndexNumber ?? Number.MAX_SAFE_INTEGER);
                }).find((candidate) => candidate.Id);
                if (!season?.Id) return;
                const episodes = await getEpisodes(credentials!, item.Id, season.Id);
                const episode = [...episodes].sort((left, right) => (left.IndexNumber ?? Number.MAX_SAFE_INTEGER) - (right.IndexNumber ?? Number.MAX_SAFE_INTEGER)).find((candidate) => candidate.Id);
                if (episode?.Id) navigation.navigate('Player', { itemId: episode.Id });
            } catch {
                // A failed episode lookup should not silently open a different
                // destination; More Info is available for manual recovery.
            }
            return;
        }
        navigation.navigate('Player', { itemId: item.Id });
    }, [credentials, navigation]);
    const reportRecommendationAction = useCallback((
        row: Extract<HomeRow, { kind: 'media' }>,
        item: JellyfinItem,
        requestedPlay: boolean,
    ) => {
        const recommendation = row.recommendation;
        if (!recommendation || !credentials || !item.Id) return;
        const event = requestedPlay && item.Type !== 'Series' ? 'play-requested' : 'item-opened';
        void reportRecommendationTelemetry(credentials, {
            sessionKey: recommendationSessionKey,
            event,
            categoryId: recommendation.categoryId,
            recommendationType: recommendation.recommendationType,
            baselineItemId: recommendation.baselineItemId,
            baselineItemName: recommendation.baselineItemName,
            mediaKind: recommendation.mediaKind,
            shelfPosition: recommendation.shelfPosition,
            itemId: item.Id,
            itemPosition: Math.max(0, row.items.findIndex((candidate) => candidate.Id === item.Id)),
            shuffleSeed: recommendationSeed,
            feedId: recommendation.feedId,
            algorithmVersion: recommendation.algorithmVersion,
            profileMode: recommendation.profileMode,
            personalization: recommendation.personalization,
        }).catch(() => undefined);
    }, [credentials, recommendationSeed, recommendationSessionKey]);
    const viewabilityConfig = useRef({
        itemVisiblePercentThreshold: 50,
        minimumViewTime: 500,
    }).current;
    const homeListRef = useRef<FlatList<HomeRow>>(null);
    const focusedHomeRow = useRef<string | null>(null);
    const visibleHomeRows = useRef<Set<string>>(new Set());
    const onViewableItemsChanged = useRef(
        ({ viewableItems }: { viewableItems: ViewToken[] }) => {
            visibleHomeRows.current = new Set(
                viewableItems
                    .filter((token) => token.isViewable)
                    .map((token) => (token.item as HomeRow).key)
                    .filter(Boolean),
            );
            const context = recommendationTelemetryContext.current;
            if (!context.credentials) return;
            for (const token of viewableItems) {
                if (!token.isViewable) continue;
                const row = token.item as HomeRow;
                if (row.kind !== 'media' || !row.recommendation) continue;
                if (impressedRecommendationRows.current.has(row.recommendation.categoryId)) continue;
                impressedRecommendationRows.current.add(row.recommendation.categoryId);
                void reportRecommendationTelemetry(context.credentials, {
                    sessionKey: context.recommendationSessionKey,
                    event: 'shelf-impression',
                    categoryId: row.recommendation.categoryId,
                    recommendationType: row.recommendation.recommendationType,
                    baselineItemId: row.recommendation.baselineItemId,
                    baselineItemName: row.recommendation.baselineItemName,
                    mediaKind: row.recommendation.mediaKind,
                    shelfPosition: row.recommendation.shelfPosition,
                    shuffleSeed: context.recommendationSeed,
                    feedId: row.recommendation.feedId,
                    algorithmVersion: row.recommendation.algorithmVersion,
                    profileMode: row.recommendation.profileMode,
                    personalization: row.recommendation.personalization,
                }).catch(() => undefined);
            }
        },
    ).current;
    useEffect(() => setNativeLazyFocusMove((direction) => {
        if (!television || (direction !== 'up' && direction !== 'down')) return false;
        const currentKey = focusedHomeRow.current;
        const currentMediaIndex = currentKey ? mediaRowFocusIds.findIndex((row) => row.key === currentKey) : -1;
        const targetMediaIndex = currentMediaIndex < 0 && direction === 'down'
            ? 0
            : currentMediaIndex + (direction === 'down' ? 1 : -1);
        const target = mediaRowFocusIds[targetMediaIndex];
        if (!target || (currentMediaIndex < 0 && direction !== 'down')) return false;
        const targetIndex = rows.findIndex((row) => row.key === target.key);
        if (targetIndex < 0) return false;
        focusedHomeRow.current = target.key;
        // Commit the outer-row position before focusing the destination. An
        // animated scroll can finish after the focus request and leave the
        // first DOWN press visually moved but semantically unfocused.
        if (!visibleHomeRows.current.has(target.key)) {
            homeListRef.current?.scrollToIndex({ index: targetIndex, animated: false });
        }
        const focusMountedTarget = (attempt: number) => {
            const focused = focusRegisteredNode(target.id);
            if (focused || attempt >= 6) return;
            setTimeout(() => focusMountedTarget(attempt + 1), 120);
        };
        setTimeout(() => focusMountedTarget(0), 120);
        return true;
    }), [mediaRowFocusIds, rows, television]);
    const renderHomeRow = useCallback<ListRenderItem<HomeRow>>(({ item }) => {
        if (item.kind === 'genres') {
            return <GenreShelf items={item.items} onPress={(genre) => genre.Id && navigation.navigate('Genre', { genreId: genre.Id, name: genre.Name ?? 'Genre' })} />;
        }
        if (item.kind === 'streaming-services') {
            return <StreamingServicesShelf onPress={(service) => navigation.navigate('Streaming', { serviceId: service.slug })} onMorePress={() => navigation.navigate('Streaming')} />;
        }
        if (item.presentation === 'continue' && continueResult.isLoading) {
            return <ContinueWatchingGhost pagePadding={pagePadding} compact={width <= 760} cardWidth={televisionContinueCardWidth} />;
        }
        const libraryRoute = item.libraryRoute;
        const mediaRowIndex = mediaRowFocusIds.findIndex((row) => row.key === item.key);
        const previousRowFocusId = mediaRowIndex > 0 ? mediaRowFocusIds[mediaRowIndex - 1]?.id : undefined;
        const nextRowFocusId = mediaRowIndex >= 0 ? mediaRowFocusIds[mediaRowIndex + 1]?.id : undefined;
        return <MediaShelf
            shelfKey={item.key}
            title={item.title}
            items={item.items}
            credentials={credentials!}
            onTitlePress={libraryRoute ? () => navigation.navigate('ServerLibrary', libraryRoute) : undefined}
            onItemPress={(media) => {
                reportRecommendationAction(item, media, false);
                openItem(media);
            }}
            onItemPlay={(media) => {
                reportRecommendationAction(item, media, true);
                playItem(media);
            }}
            landscape={item.landscape}
            posterWidth={item.presentation === 'continue' ? televisionContinueCardWidth : undefined}
            presentation={item.presentation}
            focusEntryUpId={item.presentation === 'continue'
                ? (item.key === firstContinueRow?.key ? 'home.hero.play' : previousRowFocusId)
                : (previousRowFocusId ?? (item.key === firstHomeMediaRow?.key ? 'home.hero.play' : undefined))}
            focusEntryDownId={nextRowFocusId}
            onRowFocus={() => {
                const previousKey = focusedHomeRow.current;
                const previousIndex = previousKey ? rows.findIndex((row) => row.key === previousKey) : -1;
                const rowIndex = rows.findIndex((row) => row.key === item.key);
                if (rowIndex < 0 || previousKey === item.key) return;
                focusedHomeRow.current = item.key;
                // Continue Watching is the page-top anchor. Native TV focus
                // may auto-scroll the outer list when its cards become
                // focused; restore offset zero after that focus transaction
                // so the hero -> Continue DOWN path never moves the page.
                if (item.presentation === 'continue' && (previousIndex < 0 || previousIndex > rowIndex)) {
                    setTimeout(() => homeListRef.current?.scrollToOffset({ offset: 0, animated: false }), 0);
                }
            }}
            hideLabels={!item.landscape}
            onItemIntentChange={item.key === 'continue' ? handleContinueIntent : undefined}
        />;
    }, [continueResult.isLoading, credentials, firstContinueRow?.key, firstHomeMediaRow?.key, handleContinueIntent, mediaRowFocusIds, navigation, openItem, pagePadding, playItem, reportRecommendationAction, rows, televisionContinueCardWidth, width]);

    // Reveal the hero only after its backdrop, logo decision, dimensions, and
    // accent are ready. Rotations crossfade; the first complete hero appears
    // immediately with no partially assembled content.
    const heroTransitionDuration = television ? 220 : 400;
    const revealHero = useCallback(() => {
        if (!heroRevealPending.current) return;
        heroRevealPending.current = false;
        // TV hero swaps are deliberately animated even when the global
        // reduced-motion preference is set: a snap from black to the next
        // card is especially jarring at living-room scale. Other surfaces
        // continue to honor that preference.
        if (heroTransitioning.current && (television || !preferences.reduceMotion)) {
            Animated.parallel([
                Animated.timing(heroBackdropOpacity, { toValue: 1, duration: heroTransitionDuration, useNativeDriver: true }),
                Animated.timing(heroContentOpacity, { toValue: 1, duration: heroTransitionDuration, useNativeDriver: true }),
            ]).start(() => { heroTransitioning.current = false; });
        } else {
            heroBackdropOpacity.setValue(1);
            heroContentOpacity.setValue(1);
            heroTransitioning.current = false;
        }
    }, [heroBackdropOpacity, heroContentOpacity, heroTransitionDuration, preferences.reduceMotion, television]);

    const changeHero = useCallback((direction: -1 | 1) => {
        if (heroTransitioning.current) return;
        if (television && heroCandidates.length > 1) {
            if (!credentials) return;
            const currentIndex = Math.max(0, heroCandidates.findIndex((candidate) => candidate.Id === hero?.Id));
            const nextHero = heroCandidates[(currentIndex + (direction > 0 ? 1 : -1) + heroCandidates.length) % heroCandidates.length];
            const nextBackdrop = nextHero ? imageSource(credentials, nextHero, 'Backdrop', 1920) : undefined;
            const nextLogo = nextHero?.ImageTags?.Logo ? imageSource(credentials, nextHero, 'Logo', 700) : undefined;
            heroTransitioning.current = true;
            const preload = Promise.all([
                nextBackdrop ? Image.prefetch(nextBackdrop.uri) : Promise.resolve(true),
                nextLogo ? Image.prefetch(nextLogo.uri) : Promise.resolve(true),
            ]).then(([backdropReady]) => Boolean(backdropReady)).catch(() => false);
            const preloadTimeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 2500));
            void Promise.race([preload, preloadTimeout]).then((ready) => {
                if (!ready) {
                    // Keep the current hero visible if the next asset cannot
                    // be prepared; never leave the rotation permanently busy.
                    heroTransitioning.current = false;
                    return;
                }
                Animated.parallel([
                    Animated.timing(heroBackdropOpacity, { toValue: 0, duration: preferences.reduceMotion ? 0 : heroTransitionDuration, useNativeDriver: true }),
                    Animated.timing(heroContentOpacity, { toValue: 0, duration: preferences.reduceMotion ? 0 : heroTransitionDuration, useNativeDriver: true }),
                ]).start(({ finished }) => {
                    if (!finished) { heroTransitioning.current = false; return; }
                    setLogoFailed(false);
                    setLogoValidated(false);
                    setLogoAspect(3.2);
                    setHeroOffset((value) => value + direction);
                });
            });
            return;
        }
        heroTransitioning.current = true;
        Animated.parallel([
            Animated.timing(heroBackdropOpacity, { toValue: 0, duration: preferences.reduceMotion ? 0 : heroTransitionDuration, useNativeDriver: true }),
            Animated.timing(heroContentOpacity, { toValue: 0, duration: preferences.reduceMotion ? 0 : heroTransitionDuration, useNativeDriver: true }),
        ]).start(({ finished }) => {
            if (!finished) { heroTransitioning.current = false; return; }
            setLogoFailed(false);
            setLogoValidated(false);
            setLogoAspect(3.2);
            setHeroOffset((value) => value + direction);
        });
    }, [credentials, hero, heroBackdropOpacity, heroCandidates, heroContentOpacity, heroTransitionDuration, preferences.reduceMotion, television]);

    useEffect(() => () => {
        if (credentials?.userId && latestHeroId.current) lastHeroItemByUser.set(credentials.userId, latestHeroId.current);
    }, [credentials?.userId]);

    useEffect(() => {
        if (heroCandidates.length <= 1 || scrolledPastHero || preferences.reduceMotion) return undefined;
        const timer = setTimeout(() => changeHero(1), 12000);
        return () => clearTimeout(timer);
    }, [changeHero, credentials?.userId, hero?.Id, heroCandidates.length, preferences.reduceMotion, scrolledPastHero]);

    useEffect(() => {
        if (!credentials || !hero?.Id) return undefined;
        let current = true;
        const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
        setLogoFailed(false);
        setLogoValidated(false);
        setLogoAspect(3.2);
        // Keep the outgoing sampled accent until the incoming backdrop's
        // accent is ready. Resetting to the brand color here made both the
        // ambient wash and active pagination segment flash red mid-transition.
        heroRevealPending.current = true;
        const backdrop = imageSource(credentials, hero, 'Backdrop', 1920);
        const backdropReady = backdrop
            ? Image.prefetch(backdrop.uri).catch(() => false)
            : Promise.resolve(false);
        const logoReady = (async () => {
            if (!hero.ImageTags?.Logo) return;
            const logo = imageSource(credentials, hero, 'Logo', 700);
            if (!logo) {
                if (current) setLogoFailed(true);
                return;
            }
            await Image.prefetch(logo.uri).catch(() => false);
            const usable = typeof window === 'undefined'
                ? true
                : await import('../utils/heroLogoValidation')
                    .then(({ isUsableHeroLogo }) => isUsableHeroLogo(logo.uri, controller?.signal))
                    .catch(() => false);
            if (!current) return;
            if (!usable) {
                setLogoFailed(true);
                return;
            }
            await new Promise<void>((resolve) => {
                Image.getSize(
                    logo.uri,
                    (imageWidth, imageHeight) => {
                        if (current && imageWidth > 0 && imageHeight > 0) {
                            setLogoAspect(imageWidth / imageHeight);
                        }
                        resolve();
                    },
                    () => resolve(),
                );
            });
            if (current) setLogoValidated(true);
        })();
        const accentReady = getImageAccent(credentials, hero, 'Backdrop')
            .then((accent) => { if (current) setHeroAccent(accent); })
            .catch(() => undefined);
        // TV rotations are gated by the explicit Image.prefetch in
        // changeHero. Once that gate opens, do not wait on logo validation
        // (which can involve a separate decode/readback) before revealing
        // the already-preloaded backdrop. The deadline remains for the
        // initial/non-TV path so a broken asset cannot hold the page forever.
        const deadline = setTimeout(() => {
            if (!current) return;
            setInitialHeroReady(true);
            revealHero();
        }, television ? 0 : 2500);
        void Promise.all([backdropReady, logoReady, accentReady]).then(() => {
            if (!current) return;
            clearTimeout(deadline);
            setInitialHeroReady(true);
            revealHero();
        });
        // Rotation: fade out so the next onLoad can crossfade back in.
        // First load / remount: opacity starts at 1 — no visible fade.
        if (heroTransitioning.current) {
            heroBackdropOpacity.setValue(0);
            heroContentOpacity.setValue(0);
        }
        return () => { current = false; clearTimeout(deadline); controller?.abort(); };
    }, [credentials, hero?.Id, hero?.ImageTags?.Logo, heroBackdropOpacity, heroContentOpacity, revealHero, television]);
    useEffect(() => {
        if (!heroResult.data) return;
        pageOpacity.stopAnimation();
        if (preferences.reduceMotion || !initialHeroReady) {
            pageOpacity.setValue(1);
            return;
        }
        Animated.timing(pageOpacity, {
            toValue: 1,
            duration: 210,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
        }).start();
    }, [heroResult.data, initialHeroReady, pageOpacity, preferences.reduceMotion]);

    if (!credentials) return null;
    // Gate page reveal until the hero query has data.  Once data arrives the
    // page paints fully populated — the backdrop opacity is already 1 (no
    // fade-in on first load).  On remounts, React Query's cache serves the
    // previous payload and the page renders immediately.
    if (!heroResult.data) return <SafeAreaView style={styles.safe} edges={['top']}><MatureTopNav navigation={navigation} active="Home" overlay /></SafeAreaView>;

    return (
        <SafeAreaView style={styles.safe} edges={['top']}>
            <MatureTopNav navigation={navigation} active="Home" overlay solid={navSolid} />
            <Animated.View style={[styles.pageReveal, { opacity: pageOpacity }]}>
            <FlatList
                ref={homeListRef}
                data={rows}
                keyExtractor={(item) => item.key}
                showsVerticalScrollIndicator={false}
                initialNumToRender={television ? 3 : 2}
                maxToRenderPerBatch={television ? 3 : 1}
                updateCellsBatchingPeriod={television ? 16 : 32}
                // Keep only the current TV viewport's shelves mounted. This
                // prevents nested card images from accumulating as the page
                // settles, which is critical on 1–2 GB Android TV devices.
                windowSize={television ? 3 : 3}
                // TV shelves can be numerous and each row owns several image
                // surfaces. Keep off-screen rows detached on native TV to
                // reduce traversal and memory pressure; retain all rows on
                // Web where clipping has different semantics.
                removeClippedSubviews={television}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={viewabilityConfig}
                onScroll={(event) => {
                    const offset = event.nativeEvent.contentOffset.y;
                    const pastHero = offset > 80;
                    setNavSolid((value) => value === pastHero ? value : pastHero);
                    setScrolledPastHero((value) => value === pastHero ? value : pastHero);
                }}
                scrollEventThrottle={120}
                ListHeaderComponent={homeConfig.showcase && hero ? (
                    <View style={[styles.heroShell, { height: heroHeight, marginBottom: -heroOverlap, overflow: width <= 760 ? 'hidden' : 'visible' }]}>
                        <View style={[styles.heroPlaceholder, { width, height: heroHeight + heroBlendDepth, backgroundColor: '#141414' }]} />
                        {hero?.BackdropImageTags?.length ? <Animated.Image source={imageSource(credentials, hero, 'Backdrop', 1920)} resizeMode="cover" style={[styles.heroBackdrop, { width, height: heroHeight + heroBlendDepth, opacity: heroBackdropOpacity, transform: [{ scale: 1.025 }] }]} /> : null}
                        <Svg pointerEvents="none" width={width} height={vignetteHeight} style={styles.heroVignette}>
                            <Defs>
                                <LinearGradient id="heroVertical" x1="0" y1="0" x2="0" y2="1">
                                    <Stop offset="0" stopColor="#000" stopOpacity="0.08" />
                                    <Stop offset="0.4" stopColor="#000" stopOpacity="0.08" />
                                    <Stop offset="0.68" stopColor="#000" stopOpacity="0.38" />
                                    <Stop offset="0.92" stopColor="#000" stopOpacity="1" />
                                    <Stop offset="1" stopColor="#000" stopOpacity="1" />
                                </LinearGradient>
                                <LinearGradient id="heroHorizontal" x1="0" y1="0" x2="1" y2="0">
                                    <Stop offset="0" stopColor="#000" stopOpacity="0.76" />
                                    <Stop offset="0.3" stopColor="#000" stopOpacity="0.42" />
                                    <Stop offset="0.64" stopColor="#000" stopOpacity="0" />
                                    <Stop offset="1" stopColor="#000" stopOpacity="0" />
                                </LinearGradient>
                                <RadialGradient id="heroAmbient" cx="0.16" cy="0.84" rx="0.56" ry="0.46">
                                    <Stop offset="0" stopColor={heroAccent} stopOpacity="0.24" />
                                    <Stop offset="0.48" stopColor={heroAccent} stopOpacity="0.08" />
                                    <Stop offset="1" stopColor={heroAccent} stopOpacity="0" />
                                </RadialGradient>
                            </Defs>
                            <Rect x="0" y="0" width={width} height={vignetteHeight} fill="url(#heroVertical)" />
                            <Rect x="0" y="0" width={width} height={vignetteHeight} fill="url(#heroHorizontal)" />
                            <Rect x="0" y="0" width={width} height={vignetteHeight} fill="url(#heroAmbient)" />
                        </Svg>
                        <Animated.View style={[styles.heroContent, width <= 760 && styles.heroContentMobile, centeredHero && width > 760 && styles.heroContentCentered, television && styles.heroContentTelevision, { paddingHorizontal: width <= 760 ? 0 : pagePadding, maxWidth: width <= 760 ? width : Math.min(width, browseVisuals.heroContentWidth + pagePadding * 2), opacity: heroContentOpacity }]}>
                            {hero?.ImageTags?.Logo ? (
                                logoValidated && !logoFailed ? (
                                    <Animated.Image
                                        source={imageSource(credentials, hero, 'Logo', 700)}
                                        accessibilityLabel={hero.Name ?? ''}
                                        resizeMode="contain"
                                        onLoad={(event) => {
                                            const source = event.nativeEvent.source;
                                            if (source?.width && source?.height)
                                                setLogoAspect(source.width / source.height);
                                        }}
                                        onError={() => { setLogoFailed(true); setLogoValidated(false); }}
                                        style={[
                                            styles.heroLogo,
                                            width <= 760 && styles.heroLogoMobile,
                                            centeredHero && width > 760 && styles.heroLogoCentered,
                                            {
                                                width: Math.min(width <= 760 ? 304 : 460, (width <= 760 ? 112 : 126) * logoAspect),
                                                height: width <= 760 ? 112 : 126,
                                                opacity: 1,
                                            },
                                        ]}
                                    />
                                ) : null
                            ) : (
                                <Text numberOfLines={2} style={[styles.heroTitle, width <= 760 && styles.heroTitleMobile, { color: colors.text, fontSize: browseVisuals.heroTitleSize, lineHeight: browseVisuals.heroTitleLineHeight }]}>
                                    {hero.Name}
                                </Text>
                            )}
                            {/* Show text title when logo was expected but failed to load */}
                            {hero?.ImageTags?.Logo && logoFailed ? (
                                <Text numberOfLines={2} style={[styles.heroTitle, width <= 760 && styles.heroTitleMobile, { color: colors.text, fontSize: browseVisuals.heroTitleSize, lineHeight: browseVisuals.heroTitleLineHeight }]}>
                                    {hero.Name}
                                </Text>
                            ) : null}
                            <Text style={[styles.metadata, width <= 760 && styles.metadataMobile, { fontSize: browseVisuals.heroMetadataSize }]}>{(hero.Genres ?? hero.GenreItems?.map((genre) => genre.Name).filter(Boolean) ?? []).slice(0, 3).join('   •   ')}</Text>
                            <Text numberOfLines={width <= 760 ? 2 : 3} style={[styles.overview, width <= 760 && styles.overviewMobile, { maxWidth: browseVisuals.heroContentWidth, fontSize: browseVisuals.heroOverviewSize, lineHeight: browseVisuals.heroOverviewLineHeight }]}>{hero.Overview}</Text>
                            <View style={[styles.actionRow, width <= 760 && styles.actionRowMobile]}>
                                <HeroActionButton kind="play" mobile={width <= 760} television={television} height={browseVisuals.heroActionHeight} fontSize={browseVisuals.heroActionFontSize} onPress={() => playItem(hero)} firstRowFocusId={firstRowFocusId} tvPreferredFocus />
                                <HeroActionButton kind="info" mobile={width <= 760} television={television} height={browseVisuals.heroActionHeight} fontSize={browseVisuals.heroActionFontSize} onPress={() => openItem(hero)} firstRowFocusId={firstRowFocusId} />
                                {width > 760 && hero.OfficialRating ? <Text style={[styles.rating, television && styles.ratingTelevision]}>{hero.OfficialRating}</Text> : null}
                            </View>
                        </Animated.View>
                        {width > 760 && heroCandidates.length > 1 ? <View style={[styles.carouselControls, television && styles.carouselControlsTelevision]}>
                            <FocusablePressable accessibilityLabel="Show previous featured item" onPress={() => changeHero(-1)} style={(focused) => [styles.carouselButton, focused && styles.carouselButtonFocused]}><ChevronLeft color={colors.text} size={18} /></FocusablePressable>
                            <View pointerEvents="none" style={styles.carouselPosition}>
                                {Array.from({ length: heroPageCount }).map((_, index) => <View key={index} style={[styles.carouselPositionDot, index === heroCarouselPosition && styles.carouselPositionDotActive, index === heroCarouselPosition && { backgroundColor: heroAccent }]} />)}
                            </View>
                            <FocusablePressable accessibilityLabel="Show next featured item" onPress={() => changeHero(1)} style={(focused) => [styles.carouselButton, focused && styles.carouselButtonFocused]}><ChevronRight color={colors.text} size={18} /></FocusablePressable>
                        </View> : null}
                    </View>
                ) : <View style={{ height: homeConfig.showcase ? 70 : 112 }} />}
                contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
                renderItem={renderHomeRow}
            />
            </Animated.View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    pageReveal: { flex: 1 },
    content: { paddingBottom: 72 },
    loader: { position: 'absolute', top: 180, alignSelf: 'center', zIndex: 10 },
    continueGhost: { marginBottom: 0, paddingTop: 0 },
    ghostTitle: { fontFamily: systemSansFont,  fontSize: 24, lineHeight: 32, fontWeight: '700' },
    ghostTitleMobile: { fontFamily: systemSansFont,  fontSize: 18, lineHeight: 23, fontWeight: '700' },
    ghostCard: { borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.075)' },
    ghostLine: { height: 14, marginTop: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.06)' },
    ghostLineShort: { height: 11, marginTop: 5, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.04)' },
    heroShell: { position: 'relative', backgroundColor: colors.background },
    heroPlaceholder: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
    heroBackdrop: { position: 'absolute', top: 0, left: 0 },
    heroVignette: { position: 'absolute', top: 0, left: 0 },
    heroContent: { flex: 1, justifyContent: 'flex-end', paddingBottom: 58 },
    heroContentMobile: { width: '100%', alignSelf: 'center', alignItems: 'center', paddingBottom: 18 },
    heroContentCentered: { width: '100%', alignSelf: 'center', alignItems: 'center' },
    heroContentTelevision: { paddingBottom: 72 },
    heroLogo: { maxWidth: 460, maxHeight: 126, marginBottom: 0, alignSelf: 'flex-start' },
    heroLogoMobile: { maxWidth: 304, maxHeight: 112, alignSelf: 'center' },
    heroLogoCentered: { alignSelf: 'center' },
    heroTitle: { fontFamily: systemSansFont,  color: colors.text, fontSize: 52, lineHeight: 54, fontWeight: '900', textShadowColor: '#000', textShadowRadius: 10 },
    heroTitleMobile: { fontFamily: systemSansFont,  fontSize: 38, lineHeight: 40, textAlign: 'center' },
    metadataMobile: { textAlign: 'center' },
    metadata: { fontFamily: systemSansFont,  color: '#cccccc', fontSize: 13, marginTop: spacing.xs },
    overview: { fontFamily: systemSansFont,  color: '#e2e2e2', fontSize: 14.4, lineHeight: 23, maxWidth: 650, marginTop: spacing.md, fontWeight: '500', textShadowColor: '#000', textShadowRadius: 8 },
    overviewMobile: { fontFamily: systemSansFont,  maxWidth: 350, fontSize: 13, lineHeight: 20, textAlign: 'center' },
    actionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    actionRowMobile: { alignSelf: 'stretch', justifyContent: 'center' },
    playButton: { marginTop: spacing.lg, width: 121, height: 40, borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
    heroActionFocused: { borderWidth: 2, borderColor: colors.brandBright, shadowColor: colors.brand, shadowOpacity: 0.7, shadowRadius: 12, elevation: 8, transform: [{ scale: 1.035 }] },
    heroActionFocusedTelevision: { borderWidth: 2, borderColor: 'rgba(0,0,0,0.82)', shadowColor: '#000', shadowOpacity: 0.72, shadowRadius: 5, elevation: 6, transform: [{ scale: 1.018 }] },
    playButtonPressed: { opacity: 0.82 },
    playText: { fontFamily: systemSansFont,  color: colors.background, fontSize: 16, fontWeight: '800' },
    playButtonMobile: { width: 106 },
    playButtonTelevision: { width: 150, borderRadius: 6 },
    infoButton: { marginTop: spacing.lg, minWidth: 160, height: 40, paddingHorizontal: spacing.xl, borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
    infoButtonMobile: { minWidth: 142, paddingHorizontal: spacing.md },
    infoButtonTelevision: { minWidth: 190, borderRadius: 6 },
    infoText: { fontFamily: systemSansFont,  color: colors.text, fontSize: 16 },
    heroActionFill: { ...StyleSheet.absoluteFillObject },
    heroActionContent: { zIndex: 1, flexDirection: 'row', gap: spacing.sm, alignItems: 'center', justifyContent: 'center' },
    rating: { fontFamily: systemSansFont,  color: colors.text, marginTop: spacing.lg, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderLeftWidth: 2, borderLeftColor: colors.text, backgroundColor: 'rgba(0,0,0,0.5)' },
    ratingTelevision: { fontSize: 17, lineHeight: 22, paddingHorizontal: spacing.lg },
    carouselControls: { position: 'absolute', right: 56, bottom: 60, height: 44, flexDirection: 'row', alignItems: 'center', gap: 2, padding: 4, borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)', borderRadius: 22, backgroundColor: 'rgba(8,8,8,0.66)' },
    carouselControlsTelevision: { right: 64, bottom: 72, height: 50, borderRadius: 25, padding: 5 },
    carouselPosition: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, height: 34 },
    carouselPositionDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.36)' },
    carouselPositionDotActive: { width: 20, backgroundColor: colors.brand },
    carouselButton: { width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'transparent', borderRadius: 17, backgroundColor: 'transparent' },
    carouselButtonFocused: { borderColor: colors.text, backgroundColor: 'rgba(255,255,255,0.12)' },
});
